/**
 * Human-readable phrasing for a completed half-move, shared by the move log and
 * the turn banner. `describeLogEntry` is the neutral phrase (no subject);
 * callers prepend the actor ("You", "Computer", a name).
 */
import { abilityDef, isAbilityId, pieceDef, squareName, type GameState, type LogEntry } from "../../core";

export function describeLogEntry(entry: LogEntry, state: GameState): string {
  const sq = (s: number) => squareName(s, state.config);
  switch (entry.kind) {
    case "deploy":
      return `deploys ${pieceDef(entry.piece).name} to ${sq(entry.to)}`;
    case "move": {
      const verb = entry.captured ? `takes ${pieceDef(entry.captured).name} on` : "to";
      const promo = entry.promotedTo ? `, promoting to ${pieceDef(entry.promotedTo).name}` : "";
      return `moves ${pieceDef(entry.piece).name} ${sq(entry.from)} ${verb} ${sq(entry.to)}${promo}`;
    }
    case "draw":
      return "draws a card";
    case "discard": {
      const name = isAbilityId(entry.card) ? abilityDef(entry.card).name : pieceDef(entry.card).name;
      return `discards ${name}`;
    }
    case "pass":
      return "passes";
    case "timeout":
      return "ran out of time";
    case "resign":
      return "resigns";
    case "drawAgreed":
      return "agrees to a draw";
    case "turnLimit":
      return "turn limit reached — draw";
    case "captainMove": {
      const verb = entry.captured ? `takes ${pieceDef(entry.captured).name} on` : "to";
      return `Captain ${sq(entry.from)} ${verb} ${sq(entry.to)} (free move)`;
    }
    case "card": {
      const name = abilityDef(entry.ability).name;
      if (entry.revived) return `casts ${name}, reviving ${pieceDef(entry.revived).name}`;
      if (entry.from !== null && entry.to !== null) {
        return `casts ${name} (${sq(entry.from)} to ${sq(entry.to)})`;
      }
      if (entry.from !== null) {
        const verb = entry.ability === "demolish" ? "destroying the terrain on" : "recalling the piece on";
        return `casts ${name}, ${verb} ${sq(entry.from)}`;
      }
      return `casts ${name}`;
    }
  }
}

/** Compact phrasing for the move log (keeps the terse arrow notation). */
export function describeLogEntryShort(entry: LogEntry, state: GameState): string {
  const sq = (s: number) => squareName(s, state.config);
  switch (entry.kind) {
    case "move": {
      const verb = entry.captured ? `x${pieceDef(entry.captured).name}` : "→";
      const promo = entry.promotedTo ? ` =${pieceDef(entry.promotedTo).name}` : "";
      return `${pieceDef(entry.piece).name} ${sq(entry.from)} ${verb} ${sq(entry.to)}${promo}`;
    }
    case "captainMove": {
      const verb = entry.captured ? `x${pieceDef(entry.captured).name}` : "→";
      return `Captain ${sq(entry.from)} ${verb} ${sq(entry.to)} (free)`;
    }
    default:
      return describeLogEntry(entry, state);
  }
}
